import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../context/RTLContext';
import { Button, Input } from '../UI';
import { IconEye, IconEyeOff, IconCheck, IconX } from '@tabler/icons-react';

const RegisterForm = ({ onSwitchToLogin, onRegisterSuccess }) => {
  const { register, loading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isRTL } = useRTL();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    inviteCode: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push(t('auth.validation.passwordMinLength'));
    }
    if (!/[a-z]/.test(password)) {
      errors.push(t('auth.validation.passwordLowercase') || 'One lowercase letter');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push(t('auth.validation.passwordUppercase') || 'One uppercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push(t('auth.validation.passwordNumber') || 'One number');
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      errors.push(t('auth.validation.passwordSpecial') || 'One special character');
    }
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation errors for the field being updated
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }

    // Clear auth error when user starts typing
    if (error) {
      clearError();
    }

    // Real-time password validation
    if (name === 'password') {
      const passwordErrors = validatePassword(value);
      if (passwordErrors.length > 0) {
        setValidationErrors(prev => ({
          ...prev,
          password: passwordErrors
        }));
      }
    }

    // Real-time confirm password validation
    if (name === 'confirmPassword' || (name === 'password' && formData.confirmPassword)) {
      const password = name === 'password' ? value : formData.password;
      const confirmPassword = name === 'confirmPassword' ? value : formData.confirmPassword;
      
      if (confirmPassword && password !== confirmPassword) {
        setValidationErrors(prev => ({
          ...prev,
          confirmPassword: [t('auth.validation.passwordsDoNotMatch')]
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = [t('auth.validation.nameRequired')];
    }

    if (!formData.lastName.trim()) {
      errors.lastName = [t('auth.validation.nameRequired')];
    }

    if (!formData.email.trim()) {
      errors.email = [t('auth.validation.emailRequired')];
    }

    if (!formData.inviteCode.trim()) {
      errors.inviteCode = [t('auth.validation.inviteCodeRequired')];
    }
    
    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      errors.password = passwordErrors;
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = [t('auth.validation.passwordsDoNotMatch')];
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    try {
      const { confirmPassword, ...registrationData } = formData;
      await register(registrationData);
      if (onRegisterSuccess) {
        onRegisterSuccess();
      }
      // Navigate to dashboard after successful registration
      navigate('/dashboard');
    } catch (error) {
      // Error is handled by AuthContext
      console.error('Registration failed:', error.message);
    }
  };

  return (
    <div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
              {t('forms.labels.firstName')}
            </label>
            <Input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              placeholder={t('forms.labels.firstName')}
            />
            {validationErrors.firstName && (
              <p className="mt-1 text-xs text-red-500">{validationErrors.firstName[0]}</p>
            )}
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
              {t('forms.labels.lastName')}
            </label>
            <Input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              placeholder={t('forms.labels.lastName')}
            />
            {validationErrors.lastName && (
              <p className="mt-1 text-xs text-red-500">{validationErrors.lastName[0]}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="inviteCode" className="block text-sm font-medium text-gray-700 mb-2">
            {t('auth.register.inviteCode')}
          </label>
          <Input
            type="text"
            id="inviteCode"
            name="inviteCode"
            value={formData.inviteCode}
            onChange={handleChange}
            required
            className="uppercase font-mono"
            placeholder="EYE-XXXXXXXX"
            pattern="^[A-Z]{3}-[A-Z0-9]{8}$"
          />
          {validationErrors.inviteCode && (
            <p className="mt-1 text-xs text-red-500">{validationErrors.inviteCode[0]}</p>
          )}
          <p className="text-xs mt-1 text-gray-500">
            {t('auth.register.inviteCodePlaceholder')}
          </p>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            {t('auth.register.email')}
          </label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder={t('auth.register.emailPlaceholder')}
          />
          {validationErrors.email && (
            <p className="mt-1 text-xs text-red-500">{validationErrors.email[0]}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            {t('auth.register.password')}
          </label>
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className={isRTL ? 'pl-10' : 'pr-10'}
              placeholder={t('auth.register.passwordPlaceholder')}
            />
            <button
              type="button"
              className={`absolute top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 transition-colors ${isRTL ? 'left-1' : 'right-1'}`}
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <IconEyeOff size={18} />
              ) : (
                <IconEye size={18} />
              )}
            </button>
          </div>
          {validationErrors.password && (
            <div className="mt-2 p-3 bg-red-50 rounded-lg">
              <p className="text-xs font-medium mb-2 text-gray-700">Password must contain:</p>
              {validationErrors.password.map((error, index) => (
                <div key={index} className="flex items-center gap-2 text-xs text-red-600">
                  <IconX size={12} className="text-red-500" />
                  <span>{error}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
            {t('auth.register.confirmPassword')}
          </label>
          <div className="relative">
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className={isRTL ? 'pl-10' : 'pr-10'}
              placeholder={t('auth.register.confirmPasswordPlaceholder')}
            />
            <button
              type="button"
              className={`absolute top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 transition-colors ${isRTL ? 'left-1' : 'right-1'}`}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? (
                <IconEyeOff size={18} />
              ) : (
                <IconEye size={18} />
              )}
            </button>
          </div>
          {validationErrors.confirmPassword && (
            <p className="mt-1 text-xs text-red-500">{validationErrors.confirmPassword[0]}</p>
          )}
        </div>

        <Button
          type="submit"
          disabled={loading}
          isLoading={loading}
          fullWidth
          className="mt-6"
        >
          {loading ? t('common.status.processing') : t('auth.register.submit')}
        </Button>
      </form>

      <div className="mt-8 text-center">
        <p className="text-sm text-gray-600">
          {t('auth.register.haveAccount')}{' '}
          <button
            onClick={onSwitchToLogin}
            className="text-primary hover:text-primary/80 font-medium transition-colors"
          >
            {t('auth.register.signInLink')}
          </button>
        </p>
      </div>
    </div>
  );
};

export default RegisterForm;